import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FaCheckCircle, FaClock, FaMapMarkerAlt, FaFileAlt } from 'react-icons/fa';

export default function SupplierOpportunities() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/public/rfq?supplier_id=${user.id}&show_all=${showAll}`);
      setProjects(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
      setError(error.response?.data?.detail || 'Erreur lors du chargement');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchOpportunities();
    }
  }, [user, showAll]);

  const handleArchiveProject = async (projectId) => {
    // Immediate feedback
    toast.info(i18n.language === 'fr' ? 'Masquage en cours...' : 'Hiding...');
    
    // Optimistic update - remove immediately from UI
    setProjects(prevProjects => prevProjects.filter(p => p.id !== projectId));

    try {
      await axios.post(`${API}/projects/${projectId}/archive?supplier_id=${user.id}`);
      
      // Success confirmation
      toast.success(i18n.language === 'fr' 
        ? 'Opportunité masquée avec succès' 
        : 'Opportunity hidden successfully');
        
    } catch (error) {
      console.error('Error archiving project:', error);
      toast.error(i18n.language === 'fr' 
        ? 'Erreur lors du masquage - rechargement...' 
        : 'Error hiding - reloading...');
      
      // If error, refresh the list to restore the item
      fetchOpportunities();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p className="text-gray-600">{t('loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            {error.includes('fournisseurs') && (
              <p className="text-sm text-gray-600">
                {i18n.language === 'fr' 
                  ? 'Contactez un administrateur pour obtenir le rôle fournisseur.'
                  : 'Contact an administrator to get supplier role.'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
                {i18n.language === 'fr' ? 'Opportunités de Projets' : 'Project Opportunities'}
              </h1>
              <p className="text-gray-600">
                {i18n.language === 'fr' 
                  ? 'Projets qui correspondent à vos produits et services' 
                  : 'Projects matching your products and services'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                  className="w-5 h-5 text-[#635BFF] rounded focus:ring-[#635BFF]"
                />
                <span className="text-sm font-semibold text-gray-700">
                  {i18n.language === 'fr' ? 'Voir tous les projets' : 'Show all projects'}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Projects List */}
        {projects.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-600 mb-4">
                {i18n.language === 'fr' 
                  ? 'Aucun projet disponible pour le moment' 
                  : 'No projects available at the moment'}
              </p>
              <p className="text-sm text-gray-500">
                {i18n.language === 'fr' 
                  ? 'Les projets qui correspondent à vos annonces apparaîtront ici.' 
                  : 'Projects matching your listings will appear here.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {projects.map((project) => (
              <Card key={project.id} className="hover:shadow-lg transition border-l-4 border-[#635BFF]">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-[#0A2540] mb-2">{project.project_name}</h3>
                      <p className="text-gray-600 flex items-center gap-2">
                        <FaMapMarkerAlt className="text-[#635BFF]" /> {project.location}
                        {project.site_address && ` - ${project.site_address}`}
                      </p>
                    </div>
                    <span className="px-3 py-1 bg-[#635BFF]/10 text-[#5248d9] rounded-full text-sm font-semibold">
                      {i18n.language === 'fr' ? 'Publié' : 'Published'}
                    </span>
                  </div>

                  {/* Match Details */}
                  {project.match_details && project.match_details.length > 0 && (
                    <div className="mb-4 bg-blue-50 p-3 rounded-lg">
                      <p className="text-sm font-semibold text-blue-700 mb-2">
                        ✅ {i18n.language === 'fr' ? 'Correspondances trouvées:' : 'Matches found:'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {project.match_details.slice(0, 3).map((detail, idx) => (
                          <span key={idx} className="text-xs bg-white px-2 py-1 rounded text-blue-700">
                            {detail}
                          </span>
                        ))}
                        {project.match_details.length > 3 && (
                          <span className="text-xs text-blue-600">
                            +{project.match_details.length - 3} {i18n.language === 'fr' ? 'autres' : 'more'}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Supplies Summary */}
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      {i18n.language === 'fr' ? 'Fournitures demandées:' : 'Required supplies:'}
                    </p>
                    {project.supplies && project.supplies.length > 0 && (
                      <div className="text-sm">
                        {project.supplies.slice(0, 4).map((supply, idx) => (
                          <div key={idx} className="inline-block bg-gray-100 px-3 py-1 rounded mr-2 mb-2">
                            <span className="font-semibold">{supply.name}</span>
                            <span className="text-gray-600 ml-2">
                              ({supply.quantity} {supply.unit})
                            </span>
                          </div>
                        ))}
                        {project.supplies.length > 4 && (
                          <span className="text-gray-500 text-sm">
                            +{project.supplies.length - 4} {i18n.language === 'fr' ? 'autres' : 'more'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {project.delivery_deadline && (
                    <p className="text-sm text-gray-600 mb-4">
                      <FaClock className="inline mr-2 text-[#00D4FF]" />
                      <strong>{i18n.language === 'fr' ? 'Échéance:' : 'Deadline:'}</strong> {project.delivery_deadline}
                    </p>
                  )}

                  {project.additional_notes && (
                    <p className="text-sm text-gray-600 mb-4">
                      <FaFileAlt className="inline mr-2" />
                      <strong>{i18n.language === 'fr' ? 'Notes:' : 'Notes:'}</strong> {project.additional_notes.substring(0, 100)}
                      {project.additional_notes.length > 100 && '...'}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 items-center">
                    <Link
                      to={`/supplier/rfq/${project.id}`}
                      className="bg-[#635BFF] text-white px-6 py-2 rounded-lg hover:bg-[#5248d9] font-semibold flex items-center gap-2"
                    >
                      {i18n.language === 'fr' ? '📝 Soumettre un devis' : '📝 Submit Quote'}
                    </Link>
                    <Link
                      to={`/supplier/project-detail/${project.id}`}
                      className="bg-[#0A2540] text-white px-6 py-2 rounded-lg hover:bg-[#003a52]"
                    >
                      {i18n.language === 'fr' ? 'Voir détails' : 'View Details'}
                    </Link>
                    <button
                      onClick={() => handleArchiveProject(project.id)}
                      className="ml-auto bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 flex items-center gap-2"
                      title={i18n.language === 'fr' ? 'Masquer cette opportunité' : 'Hide this opportunity'}
                    >
                      🗑️ {i18n.language === 'fr' ? 'Masquer' : 'Hide'}
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
